import { Controller } from "@hotwired/stimulus"

// Connects to data-controller="ui--date-picker"
//
// Custom calendar matching the @a-safe-digital React DatePicker / CalendarInput.
// Renders a day/month/year grid client-side (no flatpickr) using the exact
// Tailwind tokens of the React component. Supports single/multiple/range modes,
// type-to-parse input, and an optional HH:MM time field (datetime).
//
// NOTE: this is intentionally separate from `ui--datepicker` (flatpickr), which
// is still used by DateRangePickerComponent.
export default class extends Controller {
  static targets = [
    "input",
    "trigger",
    "panel",
    "grid",
    "caption",
    "time",
    "hidden",
  ]
  static values = {
    config: Object,
    selected: String,
    weekdays: Array,
    months: Array,
    monthsShort: Array,
  }

  // --- Tailwind class maps (mirror React `inputStyles`) ---
  static DAY_BASE =
    "h-9 w-9 grid place-items-center border border-transparent rounded-md text-sm " +
    "focus-visible:outline-none focus-visible:border-primary disabled:cursor-not-allowed"

  connect() {
    const cfg = this.hasConfigValue ? this.configValue : {}
    this.mode = cfg.mode || "single"
    this.fmt = cfg.dateFormat || "Y-m-d"
    this.closeOnSelect = cfg.closeOnSelect !== false
    this.enableTime = !!cfg.enableTime
    this.weekStartsOn =
      typeof cfg.weekStartsOn === "number" ? cfg.weekStartsOn : 1
    this.minDate = this.parseISODateLocal(cfg.minDate)
    this.maxDate = this.parseISODateLocal(cfg.maxDate)

    this.view = 0 // 0 = days, 1 = months, 2 = years
    this.time = { hour: 0, minute: 0 }
    // `selected` is always ISO from Ruby, so it is parsed as ISO rather than
    // through `fmt` — which is now a localized display format that would reject it.
    this.selectedDates = this.parseInitialSelection(this.selectedValue)

    if (this.selectedDates.length && this.enableTime) {
      const d = this.selectedDates[0]
      this.time = { hour: d.getHours(), minute: d.getMinutes() }
      if (this.hasTimeTarget)
        this.timeTarget.value =
          this.pad(d.getHours()) + ":" + this.pad(d.getMinutes())
    }

    this.browsingDate = this.selectedDates[0] || new Date()
    this.yearRangeStart = this.browsingDate.getFullYear()

    this._outsideClick = this.onOutsideClick.bind(this)
    this.render()
  }

  disconnect() {
    document.removeEventListener("click", this._outsideClick)
  }

  // --- Open / close ---

  toggle(event) {
    event.preventDefault()
    event.stopPropagation()
    if (this.inputTarget.disabled) return
    if (this.isOpen()) this.close()
    else this.open()
  }

  open() {
    if (!this.hasPanelTarget) return
    this.panelTarget.classList.remove("hidden")
    this.render()
    this.position()
    document.addEventListener("click", this._outsideClick)
  }

  close() {
    if (!this.hasPanelTarget) return
    this.panelTarget.classList.add("hidden")
    document.removeEventListener("click", this._outsideClick)
  }

  isOpen() {
    return this.hasPanelTarget && !this.panelTarget.classList.contains("hidden")
  }

  onOutsideClick(event) {
    if (!this.element.contains(event.target)) this.close()
  }

  // Basic flip-up when there isn't room below the input.
  position() {
    const panel = this.panelTarget
    panel.classList.remove("bottom-full", "mb-2.5", "top-full", "mt-2.5")
    const rect = this.inputTarget.getBoundingClientRect()
    const spaceBelow = window.innerHeight - rect.bottom
    if (spaceBelow < panel.offsetHeight + 16 && rect.top > panel.offsetHeight) {
      panel.classList.add("bottom-full", "mb-2.5")
    } else {
      panel.classList.add("top-full", "mt-2.5")
    }
    this.clampHorizontally()
  }

  // The panel is `absolute left-0` on the field, so a picker in the right-hand
  // column of a scroll container (a drawer) renders past its edge and is clipped.
  // Shift it back in, never past the opposite edge.
  clampHorizontally() {
    const panel = this.panelTarget
    panel.style.transform = ""
    const bounds = this.clampBounds()
    const rect = panel.getBoundingClientRect()
    let dx = 0
    if (rect.right > bounds.right) dx = bounds.right - rect.right
    if (rect.left + dx < bounds.left) dx = bounds.left - rect.left
    if (dx !== 0) panel.style.transform = `translateX(${Math.round(dx)}px)`
  }

  // Intersection of the viewport and the nearest scrollable ancestor — either can
  // be the one that clips, and they are not always the same box.
  clampBounds() {
    const GAP = 8
    let left = 0
    let right = window.innerWidth
    let el = this.element.parentElement
    while (el && el !== document.body) {
      const style = getComputedStyle(el)
      if (/auto|hidden|scroll/.test(style.overflowX + style.overflowY)) {
        const box = el.getBoundingClientRect()
        left = Math.max(left, box.left)
        right = Math.min(right, box.right)
        break
      }
      el = el.parentElement
    }
    return { left: left + GAP, right: right - GAP }
  }

  // --- Header navigation ---

  toggleView(event) {
    event.preventDefault()
    this.view = (this.view + 1) % 3
    if (this.view === 2) this.yearRangeStart = this.browsingDate.getFullYear()
    this.render()
  }

  prev(event) {
    event.preventDefault()
    if (this.view === 0)
      this.browsingDate = this.addMonths(this.browsingDate, -1)
    else if (this.view === 1)
      this.browsingDate = this.addYears(this.browsingDate, -1)
    else this.yearRangeStart -= 12
    this.render()
  }

  next(event) {
    event.preventDefault()
    if (this.view === 0)
      this.browsingDate = this.addMonths(this.browsingDate, 1)
    else if (this.view === 1)
      this.browsingDate = this.addYears(this.browsingDate, 1)
    else this.yearRangeStart += 12
    this.render()
  }

  // --- Selection ---

  selectDay(event) {
    event.preventDefault()
    // Stop the click from bubbling to the document-level outside-click handler.
    // render() below replaces the grid's innerHTML, detaching the clicked button;
    // once the node is orphaned, this.element.contains(event.target) returns false,
    // so the still-bubbling click is misread as a click *outside* the picker and
    // closes the panel — dropping the user on the default 00:00 before they can
    // reach the time field. CONEK-4920.
    event.stopPropagation()
    const date = this.parseISODateLocal(event.currentTarget.dataset.date)
    if (!date) return
    this.applyTime(date)
    this.browsingDate = date

    if (this.mode === "multiple") {
      const idx = this.selectedDates.findIndex((d) => this.sameDay(d, date))
      if (idx >= 0) this.selectedDates.splice(idx, 1)
      else this.selectedDates.push(date)
      this.selectedDates.sort((a, b) => a - b)
    } else if (this.mode === "range") {
      if (this.selectedDates.length === 1) {
        this.selectedDates.push(date)
        this.selectedDates.sort((a, b) => a - b)
      } else {
        this.selectedDates = [date]
      }
    } else {
      this.selectedDates = [date]
    }

    this.updateInputValue()
    // In range mode the first click selects the start only — dispatching there
    // would carry a bare start ISO (a one-element " to " join emits no
    // separator) and let the host's auto-submit fire a half range mid-selection,
    // tearing the open calendar down before the end-date click. Every
    // single/multiple click is a completion, so only range waits for its pair.
    if (this.mode !== "range" || this.selectedDates.length === 2)
      this.dispatchChange()
    this.render()

    // Datetime mode: keep the panel open after picking a day so the user can
    // set the time — otherwise it would close with the default 00:00 before the
    // time field is reachable. Move focus straight to the time field to invite
    // completion. The panel then closes on outside-click / Enter / Escape.
    if (this.enableTime && this.hasTimeTarget) {
      this.timeTarget.focus()
      // Immediately pop the native time UI so the user lands straight in it after
      // choosing a day. showPicker() needs the current user activation — this runs
      // inside the day-click handler, so it's present — and is unsupported on some
      // browsers; guard so a failure just leaves the field focused, not throwing.
      try {
        this.timeTarget.showPicker()
      } catch {
        // no-op: browser lacks showPicker() or the user activation was consumed
      }
      return
    }

    const isComplete =
      this.mode === "single" ||
      (this.mode === "range" && this.selectedDates.length === 2)
    if (this.closeOnSelect && this.mode === "single" && isComplete) this.close()
  }

  selectMonth(event) {
    event.preventDefault()
    // Same orphaned-node trap as selectDay: render() below replaces the grid, so
    // a still-bubbling click reads as a click outside and closes the panel — on
    // the day view the user just navigated to, before they can pick a day.
    event.stopPropagation()
    const month = parseInt(event.currentTarget.dataset.month, 10)
    this.browsingDate = new Date(this.browsingDate.getFullYear(), month, 1)
    this.view = 0
    this.render()
  }

  selectYear(event) {
    event.preventDefault()
    event.stopPropagation()
    const year = parseInt(event.currentTarget.dataset.year, 10)
    this.browsingDate = new Date(year, this.browsingDate.getMonth(), 1)
    this.view = 0
    this.render()
  }

  // --- Text input ---

  onInput() {
    const value = this.inputTarget.value
    if (value.trim() === "") {
      this.selectedDates = []
      this.syncHidden()
      this.dispatchChange()
      this.render()
      return
    }
    const dates = this.parseSelection(value)
    if (dates.length) {
      this.selectedDates = dates
      this.browsingDate = dates[0]
      this.syncHidden()
      this.render()
      this.dispatchChange()
    }
  }

  onKeydown(event) {
    if (event.key === "Escape" && this.isOpen()) {
      event.preventDefault()
      this.close()
    } else if (event.key === "Enter") {
      event.preventDefault()
      this.close()
    }
  }

  onTimeInput() {
    const [h, m] = this.timeTarget.value.split(":").map((n) => parseInt(n, 10))
    if (Number.isNaN(h) || Number.isNaN(m)) return
    this.time = { hour: h, minute: m }
    this.selectedDates = this.selectedDates.map((d) => {
      const nd = new Date(d)
      nd.setHours(h, m, 0, 0)
      return nd
    })
    this.updateInputValue()
    this.dispatchChange()
  }

  // --- Rendering ---

  render() {
    if (this.hasCaptionTarget)
      this.captionTarget.textContent = this.captionLabel()
    if (this.hasGridTarget) {
      if (this.view === 1) this.grid_target_html(this.renderMonths())
      else if (this.view === 2) this.grid_target_html(this.renderYears())
      else this.grid_target_html(this.renderDays())
    }
  }

  grid_target_html(html) {
    this.gridTarget.innerHTML = html
  }

  captionLabel() {
    if (this.view === 2)
      return `${this.yearRangeStart} - ${this.yearRangeStart + 11}`
    if (this.view === 1) return `${this.browsingDate.getFullYear()}`
    const month = this.capitalize(
      this.monthsValue[this.browsingDate.getMonth()] || "",
    )
    return `${month} ${this.browsingDate.getFullYear()}`
  }

  renderDays() {
    const head = this.weekdaysValue
      .map(
        (w) =>
          `<div class="text-muted-foreground w-9 font-normal text-xs grid place-items-center">${this.capitalize(w)}</div>`,
      )
      .join("")

    const cells = this.monthCells(this.browsingDate)
    let rows = ""
    for (let i = 0; i < cells.length; i += 7) {
      const week = cells
        .slice(i, i + 7)
        .map((c) => this.dayButton(c))
        .join("")
      rows += `<div class="flex justify-between mt-2">${week}</div>`
    }

    return (
      `<div class="flex flex-col w-full gap-4 mt-2"><div class="flex flex-col gap-y-2">` +
      `<div class="flex flex-col w-full text-sm gap-y-1">` +
      `<div class="flex justify-between">${head}</div>${rows}</div></div></div>`
    )
  }

  dayButton(cell) {
    const { date, outside } = cell
    const disabled = this.isDisabled(date)
    const selected = this.isSelected(date)
    const middle = this.isRangeMiddle(date)
    const today = this.sameDay(date, new Date())

    let cls = this.constructor.DAY_BASE
    if (disabled) {
      cls += " text-muted-foreground opacity-50 pointer-events-none"
    } else if (selected) {
      cls +=
        " bg-primary/10 text-primary hover:bg-primary/10 hover:text-primary"
    } else if (middle) {
      cls += " bg-accent text-accent-foreground"
    } else if (outside) {
      cls += " text-muted-foreground opacity-50 hover:bg-input"
    } else if (today) {
      cls += " bg-input/50 text-foreground-medium hover:bg-input"
    } else {
      cls += " text-foreground hover:bg-input"
    }

    const aria = selected ? ' aria-selected="true"' : ""
    return (
      `<button type="button" class="${cls}" data-date="${this.toISODate(date)}"` +
      `${aria} data-action="ui--date-picker#selectDay"${disabled ? " disabled" : ""}>` +
      `${date.getDate()}</button>`
    )
  }

  renderMonths() {
    const options = this.monthsShortValue
      .map(
        (label, i) =>
          `<button type="button" data-month="${i}" data-action="ui--date-picker#selectMonth" ` +
          `class="${this.optionClasses()}">${this.capitalize(label)}</button>`,
      )
      .join("")
    return `<div class="grid grid-cols-3 gap-2.5 text-center mt-2">${options}</div>`
  }

  renderYears() {
    let options = ""
    for (let i = 0; i < 12; i++) {
      const year = this.yearRangeStart + i
      options +=
        `<button type="button" data-year="${year}" data-action="ui--date-picker#selectYear" ` +
        `class="${this.optionClasses()}">${year}</button>`
    }
    return `<div class="grid grid-cols-3 gap-2.5 text-center mt-2">${options}</div>`
  }

  optionClasses() {
    return (
      "mx-auto rounded-md px-3 py-1.5 text-sm text-foreground-medium " +
      "hover:bg-primary hover:text-white focus-visible:outline focus-visible:outline-primary"
    )
  }

  // --- Date helpers ---

  // 42-cell (6 weeks) grid for the month containing `date`, aligned to weekStartsOn.
  monthCells(date) {
    const year = date.getFullYear()
    const month = date.getMonth()
    const first = new Date(year, month, 1)
    const offset = (first.getDay() - this.weekStartsOn + 7) % 7
    const start = new Date(year, month, 1 - offset)
    const cells = []
    for (let i = 0; i < 42; i++) {
      const d = new Date(
        start.getFullYear(),
        start.getMonth(),
        start.getDate() + i,
      )
      cells.push({ date: d, outside: d.getMonth() !== month })
    }
    return cells
  }

  isSelected(date) {
    return this.selectedDates.some((d) => this.sameDay(d, date))
  }

  isRangeMiddle(date) {
    if (this.mode !== "range" || this.selectedDates.length < 2) return false
    const [start, end] = this.selectedDates
    return date > this.midnight(start) && date < this.midnight(end)
  }

  isDisabled(date) {
    const t = this.midnight(date).getTime()
    if (this.minDate && t < this.midnight(this.minDate).getTime()) return true
    if (this.maxDate && t > this.midnight(this.maxDate).getTime()) return true
    return false
  }

  applyTime(date) {
    if (this.enableTime) date.setHours(this.time.hour, this.time.minute, 0, 0)
  }

  sameDay(a, b) {
    return (
      a.getFullYear() === b.getFullYear() &&
      a.getMonth() === b.getMonth() &&
      a.getDate() === b.getDate()
    )
  }

  midnight(date) {
    return new Date(date.getFullYear(), date.getMonth(), date.getDate())
  }

  addMonths(date, n) {
    return new Date(date.getFullYear(), date.getMonth() + n, 1)
  }

  addYears(date, n) {
    return new Date(date.getFullYear() + n, date.getMonth(), 1)
  }

  // --- Value serialization (mirrors flatpickr defaults for backward-compat) ---

  updateInputValue() {
    this.inputTarget.value = this.formatSelection()
    this.syncHidden()
  }

  // The hidden field is what the server reads, so it carries ISO regardless of
  // how the visible text is formatted for the user's locale.
  syncHidden() {
    if (!this.hasHiddenTarget) return
    this.hiddenTarget.value = this.selectedDates
      .map((d) => this.isoValue(d))
      .join(this.mode === "range" ? " to " : ", ")
  }

  isoValue(date) {
    const day = this.toISODate(date)
    if (!this.enableTime) return day
    return `${day}T${this.pad(date.getHours())}:${this.pad(date.getMinutes())}`
  }

  // Only the hidden-field variant renders an ISO `selected` value; a caller that
  // passed its own `format:` still submits the text, so its value arrives in that
  // format and must keep going through parseSelection (hour included).
  parseInitialSelection(value) {
    if (!this.hasHiddenTarget) return this.parseSelection(value)
    if (!value) return []
    const tokens =
      this.mode === "range" ? value.split(/\s+to\s+/) : value.split(/\s*,\s*/)
    return tokens.map((t) => this.parseISO(t.trim())).filter(Boolean)
  }

  parseISO(str) {
    const m = String(str).match(
      /^(\d{4})-(\d{2})-(\d{2})(?:[T ](\d{2}):(\d{2}))?/,
    )
    if (!m) return null
    return new Date(
      parseInt(m[1], 10),
      parseInt(m[2], 10) - 1,
      parseInt(m[3], 10),
      m[4] ? parseInt(m[4], 10) : 0,
      m[5] ? parseInt(m[5], 10) : 0,
    )
  }

  formatSelection() {
    if (!this.selectedDates.length) return ""
    if (this.mode === "multiple")
      return this.selectedDates.map((d) => this.formatDate(d)).join(", ")
    if (this.mode === "range") {
      const parts = this.selectedDates.map((d) => this.formatDate(d))
      return parts.join(" to ")
    }
    return this.formatDate(this.selectedDates[0])
  }

  parseSelection(value) {
    if (!value) return []
    let tokens
    if (this.mode === "range") tokens = value.split(/\s+to\s+/)
    else if (this.mode === "multiple") tokens = value.split(/\s*,\s*/)
    else tokens = [value]
    return tokens.map((t) => this.parseDate(t.trim())).filter(Boolean)
  }

  dispatchChange() {
    this.dispatch("change", {
      detail: {
        value: this.formatSelection(),
        dates: this.selectedDates.slice(),
      },
    })
  }

  // --- Minimal strftime-token (flatpickr-style) format / parse ---
  // Supports Y y m d H h i S K — sufficient for the default "Y-m-d" and
  // "Y-m-d H:i" formats. Exotic tokens degrade gracefully (no parse).

  formatDate(date) {
    const map = {
      Y: date.getFullYear(),
      y: this.pad(date.getFullYear() % 100),
      m: this.pad(date.getMonth() + 1),
      d: this.pad(date.getDate()),
      H: this.pad(date.getHours()),
      h: this.pad(((date.getHours() + 11) % 12) + 1),
      i: this.pad(date.getMinutes()),
      S: this.pad(date.getSeconds()),
      K: date.getHours() < 12 ? "AM" : "PM",
    }
    return this.fmt.replace(/[YymdHhiSK]/g, (tok) => map[tok])
  }

  parseDate(str) {
    if (!str) return null
    const tokenRe = {
      Y: "(\\d{4})",
      y: "(\\d{2})",
      m: "(\\d{1,2})",
      d: "(\\d{1,2})",
      H: "(\\d{1,2})",
      h: "(\\d{1,2})",
      i: "(\\d{1,2})",
      S: "(\\d{1,2})",
      K: "(AM|PM)",
    }
    const order = []
    let pattern = "^"
    for (const ch of this.fmt) {
      if (tokenRe[ch]) {
        pattern += tokenRe[ch]
        order.push(ch)
      } else {
        pattern += ch.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")
      }
    }
    pattern += "$"

    const match = str.match(new RegExp(pattern))
    if (!match) {
      const fallback = new Date(str)
      return Number.isNaN(fallback.getTime()) ? null : fallback
    }

    const parts = {
      year: 1970,
      month: 0,
      day: 1,
      hour: 0,
      minute: 0,
      second: 0,
    }
    order.forEach((tok, i) => {
      const v = match[i + 1]
      if (tok === "Y") parts.year = parseInt(v, 10)
      else if (tok === "y") parts.year = 2000 + parseInt(v, 10)
      else if (tok === "m") parts.month = parseInt(v, 10) - 1
      else if (tok === "d") parts.day = parseInt(v, 10)
      else if (tok === "H") parts.hour = parseInt(v, 10)
      else if (tok === "h") parts.hour = parseInt(v, 10)
      else if (tok === "i") parts.minute = parseInt(v, 10)
      else if (tok === "S") parts.second = parseInt(v, 10)
      else if (tok === "K" && v === "PM") parts.hour = (parts.hour % 12) + 12
    })

    const date = new Date(
      parts.year,
      parts.month,
      parts.day,
      parts.hour,
      parts.minute,
      parts.second,
    )
    // Reject impossible dates (e.g. month 13) — they roll over on construction.
    if (date.getMonth() !== parts.month || date.getDate() !== parts.day)
      return null
    return date
  }

  parseISODateLocal(str) {
    if (!str) return null
    const m = String(str).match(/^(\d{4})-(\d{2})-(\d{2})/)
    if (m)
      return new Date(
        parseInt(m[1], 10),
        parseInt(m[2], 10) - 1,
        parseInt(m[3], 10),
      )
    const d = new Date(str)
    return Number.isNaN(d.getTime()) ? null : d
  }

  toISODate(date) {
    return `${date.getFullYear()}-${this.pad(date.getMonth() + 1)}-${this.pad(date.getDate())}`
  }

  pad(n) {
    return String(n).padStart(2, "0")
  }

  capitalize(s) {
    return s ? s.charAt(0).toUpperCase() + s.slice(1) : s
  }
}
